import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { AiOutlineButton } from 'src/components/widgets/ai';
import { GhostButton } from 'src/components/widgets/buttons';
import { QuestionDialog, useDialogManager } from 'src/components/widgets/dialog';
import { Icon } from 'src/components/widgets/icon';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import DeleteIcon from 'src/shared/material_icons/action/delete.svg';
import AddIcon from 'src/shared/material_icons/content/add.svg';
import EditIcon from 'src/shared/material_icons/editor/mode_edit.svg';
import ExpandMoreIcon from 'src/shared/material_icons/navigation/expand_more.svg';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { AgentApiError, deleteAgentConversation, listAgentConversations, renameAgentConversation } from './agentClient';
import { AgentConversationSummary } from './types';

const PAGE_SIZE = 20;
const DELETE_DIALOG_ID = 'agent-delete-conversation';
// The server accepts one deletion at a time: after each one the trash waits this long, unless the server says how long to wait.
const DELETE_COOLDOWN_SECONDS = 10;

// Keeps the tooltip on the disabled trash: a disabled button does not receive the pointer.
const DeleteButtonWrap = styled.span`
    display: inline-flex;

    button:disabled {
        pointer-events: none;
    }
`;

// The history drops down from the conversation name, over the conversation, about a third of the column wide.
const AccordionPanel = styled.aside`
    position: absolute;
    z-index: 50;
    top: calc(100% + 4px);
    left: 0;
    display: flex;
    width: 360px;
    max-width: 80vw;
    max-height: min(420px, 60vh);
    flex-direction: column;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: white;
    box-shadow: 0 18px 48px rgba(15, 23, 42, 0.28);
`;

// Persistent left rail: the conversation list lives in its own column beside the conversation, filling the height.
const RailPanel = styled.aside`
    display: flex;
    width: 100%;
    flex: 1;
    min-height: 0;
    flex-direction: column;
    background: white;
    border-right: 1px solid #e5e7eb;
`;

const RailHeader = styled.div`
    flex: none;
    padding: 12px;
    border-bottom: 1px solid #eef1f4;
`;

// Full-width toggle that opens the other (non-active) conversations under the current one.
const OthersToggle = styled.button<{ $open: boolean }>`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 8px 12px;
    border: 0;
    background: transparent;
    color: #607d8b;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        color: #263238;
    }

    ${Icon},
    ${Icon} svg {
        width: 16px;
        height: 16px;
        transition: transform 0.15s ease;
        transform: rotate(${(props) => (props.$open ? 180 : 0)}deg);
    }
`;

const RailList = styled.div`
    display: flex;
    overflow-y: auto;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    gap: 6px;
    padding: 8px;
`;

const NewConversationButton = styled.button`
    display: inline-flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    border: 1px solid ${theme.ai.surfaceBorder};
    border-radius: 8px;
    background: white;
    color: ${theme.ai.solid};
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: ${theme.ai.solid};
        background: #f5f3ff;
    }

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const IconButton = styled.button<{ $danger?: boolean }>`
    display: inline-flex;
    width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: #607d8b;
    cursor: pointer;

    ${Icon},
    ${Icon} svg {
        width: 16px;
        height: 16px;
        min-width: 16px;
        fill: currentColor;
    }

    &:hover:not(:disabled) {
        background: ${(props) => (props.$danger ? '#fff5f5' : '#f5f3ff')};
        color: ${(props) => (props.$danger ? '#de1c22' : theme.ai.solid)};
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const List = styled.div`
    display: flex;
    overflow-y: auto;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
`;

const ConversationRow = styled.div<{ $active: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid ${(props) => (props.$active ? '#c4b5fd' : 'transparent')};
    border-radius: 9px;
    background: ${(props) => (props.$active ? '#f5f3ff' : 'transparent')};

    &:hover {
        background: ${(props) => (props.$active ? '#f5f3ff' : '#f8fafc')};
    }
`;

const ConversationButton = styled.button`
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 3px;
    padding: 10px;
    border: 0;
    background: transparent;
    color: #263238;
    cursor: pointer;
    text-align: left;

    &:disabled {
        cursor: not-allowed;
    }
`;

const RenameArea = styled.div`
    display: flex;
    min-width: 0;
    flex: 1;
    padding: 10px;
`;

const ConversationName = styled.span`
    overflow: hidden;
    width: 100%;
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ConversationMeta = styled.span`
    color: #78909c;
    font-size: 11px;
`;

const RenameInput = styled.input`
    min-width: 0;
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #a78bfa;
    border-radius: 6px;
    outline: none;
    font: inherit;
`;

const Message = styled.div`
    padding: 24px 12px;
    color: #78909c;
    font-size: 13px;
    text-align: center;
`;

const Footer = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px 12px 14px;
`;

export type ConversationHistoryItem<Id extends string | number> = Omit<AgentConversationSummary, 'conversationId' | 'status' | 'draftId'> & {
    conversationId: Id;
};
export interface ConversationHistorySource<Id extends string | number> {
    list: (
        pageNumber: number,
        pageSize: number,
        signal?: AbortSignal
    ) => Promise<{ items: ConversationHistoryItem<Id>[]; pageNumber: number; hasMore: boolean }>;
    rename?: (id: Id, name: string) => Promise<{ conversationId: Id; name: string }>;
    delete?: (id: Id) => Promise<void>;
}
interface ConversationHistoryProps<Id extends string | number> {
    source: ConversationHistorySource<Id>;
    id?: string;
    /** Clicks on the toggle this dropdown hangs from do not count as clicking outside it. */
    anchorRef?: RefObject<HTMLElement | null>;
    activeConversationId: Id | null;
    disabled: boolean;
    revision: number;
    /** `accordion` drops down from the conversation name; `rail` is a persistent left column. */
    variant?: 'accordion' | 'rail';
    onClose: () => void;
    onNewConversation?: () => void;
    onSelectConversation: (conversationId: Id) => void;
    onRenamed?: (conversationId: Id, name: string) => void;
    onDeleted?: (conversationId: Id) => void;
}

export const ConversationHistory = <Id extends string | number>({
    source,
    id,
    anchorRef,
    activeConversationId,
    disabled,
    revision,
    variant = 'accordion',
    onClose,
    onNewConversation,
    onSelectConversation,
    onRenamed,
    onDeleted
}: ConversationHistoryProps<Id>) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [items, setItems] = useState<ConversationHistoryItem<Id>[]>([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<Id | null>(null);
    const [editingName, setEditingName] = useState('');
    const [isRenaming, setIsRenaming] = useState(false);
    const [deletingId, setDeletingId] = useState<Id | null>(null);
    const [isDeleteCoolingDown, setIsDeleteCoolingDown] = useState(false);
    const [othersOpen, setOthersOpen] = useState(false);
    const deleteCooldownTimerRef = useRef<number | undefined>(undefined);

    useEffect(() => () => window.clearTimeout(deleteCooldownTimerRef.current), []);

    const startDeleteCooldown = (seconds: number) => {
        window.clearTimeout(deleteCooldownTimerRef.current);
        setIsDeleteCoolingDown(true);
        deleteCooldownTimerRef.current = window.setTimeout(() => setIsDeleteCoolingDown(false), seconds * 1000);
    };
    const containerRef = useRef<HTMLElement>(null);
    // The delete confirmation lives in a dialog outside the dropdown: clicking in it must not close the dropdown.
    const isConfirmingDeleteRef = useRef(false);

    useEffect(() => {
        if (variant === 'rail') return;
        const closeOnOutsideClick = (event: PointerEvent) => {
            const target = event.target as Node;
            if (isConfirmingDeleteRef.current || containerRef.current?.contains(target) || anchorRef?.current?.contains(target)) return;
            onClose();
        };
        document.addEventListener('pointerdown', closeOnOutsideClick);
        return () => document.removeEventListener('pointerdown', closeOnOutsideClick);
    }, [anchorRef, onClose, variant]);

    const loadPage = useCallback(
        async (page: number, append: boolean, signal?: AbortSignal) => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await source.list(page, PAGE_SIZE, signal);
                if (signal?.aborted) return;
                setItems((current) => (append ? [...current, ...response.items] : response.items));
                setPageNumber(response.pageNumber);
                setHasMore(response.hasMore);
            } catch (loadError) {
                if ((loadError as Error).name !== 'AbortError') {
                    setError((loadError as Error).message || T._('Conversation history could not be loaded.', 'Admin'));
                }
            } finally {
                if (!signal?.aborted) setIsLoading(false);
            }
        },
        [source]
    );

    useEffect(() => {
        const controller = new AbortController();
        void loadPage(1, false, controller.signal);
        return () => controller.abort();
    }, [loadPage, revision]);

    const beginRename = (item: ConversationHistoryItem<Id>) => {
        setEditingId(item.conversationId);
        setEditingName(item.name ?? '');
    };

    const commitRename = async () => {
        const name = editingName.trim();
        if (!editingId || !name || !source.rename || isRenaming) return;
        setIsRenaming(true);
        setError(null);
        try {
            const renamed = await source.rename(editingId, name);
            setItems((current) => current.map((item) => (item.conversationId === renamed.conversationId ? { ...item, name: renamed.name } : item)));
            onRenamed?.(renamed.conversationId, renamed.name);
            setEditingId(null);
        } catch (renameError) {
            setError((renameError as Error).message || T._('The conversation could not be renamed.', 'Admin'));
        } finally {
            setIsRenaming(false);
        }
    };

    const deleteConversation = async (conversationId: Id) => {
        if (!source.delete) return;
        setDeletingId(conversationId);
        setError(null);
        try {
            await source.delete(conversationId);
            setItems((current) => current.filter((item) => item.conversationId !== conversationId));
            onDeleted?.(conversationId);
            startDeleteCooldown(DELETE_COOLDOWN_SECONDS);
        } catch (deleteError) {
            // 405/429: the previous deletion is still being processed. Not an error: the trash just waits.
            if (deleteError instanceof AgentApiError && (deleteError.status === 405 || deleteError.status === 429)) {
                startDeleteCooldown(deleteError.retryAfterSeconds ?? DELETE_COOLDOWN_SECONDS);
            } else {
                setError(T._('The conversation could not be deleted.', 'Admin'));
            }
        } finally {
            setDeletingId(null);
        }
    };

    const confirmDelete = (item: ConversationHistoryItem<Id>) => {
        const closeConfirm = () => {
            isConfirmingDeleteRef.current = false;
            closeDialog(DELETE_DIALOG_ID);
        };
        isConfirmingDeleteRef.current = true;
        showDialog(
            DELETE_DIALOG_ID,
            <QuestionDialog
                title={T._('Delete conversation?', 'Admin')}
                message={T._('"{0}" will be removed from your history. This cannot be undone.', 'Admin').replace(
                    '{0}',
                    item.name || T._('Untitled conversation', 'Admin')
                )}
                buttonYesLabel={T._('Delete', 'Admin')}
                buttonNoLabel={T._('Cancel', 'Admin')}
                destructive
                onClose={closeConfirm}
                onYesClick={() => {
                    closeConfirm();
                    void deleteConversation(item.conversationId);
                }}
                onNoClick={closeConfirm}
            />
        );
    };

    const renderRow = (item: ConversationHistoryItem<Id>) => {
        const isEditing = editingId === item.conversationId;
        return (
            <ConversationRow key={item.conversationId} $active={item.conversationId === activeConversationId}>
                {isEditing ? (
                    <RenameArea>
                        <RenameInput
                            autoFocus
                            value={editingName}
                            maxLength={200}
                            disabled={isRenaming}
                            onChange={(event) => setEditingName(event.currentTarget.value)}
                            onBlur={() => setEditingId(null)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') void commitRename();
                                if (event.key === 'Escape') setEditingId(null);
                            }}
                        />
                    </RenameArea>
                ) : (
                    <ConversationButton type='button' disabled={disabled} onClick={() => onSelectConversation(item.conversationId)}>
                        <>
                            <ConversationName>{item.name || T._('Untitled conversation', 'Admin')}</ConversationName>
                            <ConversationMeta>
                                {typeof item.conversationId === 'number' ? `#${item.conversationId} · ` : ''}
                                {new Date(item.lastActivityOn).toLocaleString()} · {item.turnCount}{' '}
                                {item.turnCount === 1 ? T._('turn', 'Admin') : T._('turns', 'Admin')}
                            </ConversationMeta>
                        </>
                    </ConversationButton>
                )}
                {!isEditing && source.rename && (
                    <ZTooltip position='left' title={T._('Rename', 'Admin')}>
                        <IconButton type='button' aria-label={T._('Rename', 'Admin')} disabled={disabled} onClick={() => beginRename(item)}>
                            <Icon>
                                <EditIcon />
                            </Icon>
                        </IconButton>
                    </ZTooltip>
                )}
                {!isEditing && source.delete && (
                    <ZTooltip
                        position='left'
                        title={
                            isDeleteCoolingDown || deletingId !== null
                                ? T._('Please wait a few seconds before deleting another conversation.', 'Admin')
                                : T._('Delete', 'Admin')
                        }
                    >
                        <DeleteButtonWrap>
                            <IconButton
                                type='button'
                                $danger
                                aria-label={T._('Delete', 'Admin')}
                                disabled={disabled || isDeleteCoolingDown || deletingId !== null}
                                onClick={() => confirmDelete(item)}
                            >
                                <Icon>
                                    <DeleteIcon />
                                </Icon>
                            </IconButton>
                        </DeleteButtonWrap>
                    </ZTooltip>
                )}
            </ConversationRow>
        );
    };

    const activeItem = items.find((item) => item.conversationId === activeConversationId) ?? null;
    const otherItems = items.filter((item) => item.conversationId !== activeConversationId);

    const Container = variant === 'rail' ? RailPanel : AccordionPanel;

    return (
        <Container ref={containerRef} id={id} aria-label={T._('Conversation history', 'Admin')}>
            {variant === 'rail' ? (
                <>
                    <RailHeader>
                        <NewConversationButton type='button' disabled={disabled} onClick={onNewConversation}>
                            <Icon>
                                <AddIcon />
                            </Icon>
                            {T._('New conversation', 'Admin')}
                        </NewConversationButton>
                    </RailHeader>
                    <RailList>
                        {error && <Message>{error}</Message>}
                        {!error && isLoading && items.length === 0 && <Message>{T._('Loading conversations…', 'Admin')}</Message>}
                        {activeItem && renderRow(activeItem)}
                        {otherItems.length > 0 && (
                            <OthersToggle type='button' $open={othersOpen} aria-expanded={othersOpen} onClick={() => setOthersOpen((open) => !open)}>
                                <span>
                                    {othersOpen ? T._('Other conversations', 'Admin') : `${T._('Other conversations', 'Admin')} · +${otherItems.length}`}
                                </span>
                                <Icon>
                                    <ExpandMoreIcon />
                                </Icon>
                            </OthersToggle>
                        )}
                        {othersOpen && otherItems.map(renderRow)}
                        {!error && !isLoading && !activeItem && otherItems.length === 0 && <Message>{T._('No previous conversations yet.', 'Admin')}</Message>}
                    </RailList>
                </>
            ) : (
                <List>
                    {error && <Message>{error}</Message>}
                    {!error && isLoading && items.length === 0 && <Message>{T._('Loading conversations…', 'Admin')}</Message>}
                    {!error && !isLoading && items.length === 0 && <Message>{T._('No previous conversations yet.', 'Admin')}</Message>}
                    {items.map(renderRow)}
                </List>
            )}

            {hasMore && (
                <Footer>
                    <AiOutlineButton type='button' $size='small' disabled={isLoading} onClick={() => void loadPage(pageNumber + 1, true)}>
                        {isLoading ? T._('Loading…', 'Admin') : T._('Load more', 'Admin')}
                    </AiOutlineButton>
                </Footer>
            )}
            {error && (
                <Footer>
                    <GhostButton type='button' $size='small' onClick={() => void loadPage(1, false)}>
                        {T._('Try again', 'Admin')}
                    </GhostButton>
                </Footer>
            )}
        </Container>
    );
};

const agentHistorySource: ConversationHistorySource<number> = {
    list: listAgentConversations,
    rename: renameAgentConversation,
    delete: deleteAgentConversation
};
export const AgentConversationHistory = (props: Omit<ConversationHistoryProps<number>, 'source'>) => (
    <ConversationHistory {...props} source={agentHistorySource} />
);
